import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from 'zustand';
import type { AudienceTargetCountStatus } from '@/components/audiences/audience-target-count';
import { mapTreeToFilterNodeDto } from '@/components/audiences/map-tree-to-filter-dto';
import { toast } from '@/components/ui/sonner';
import { useCreateAudience, usePreviewAudience } from '@/hooks/use-audiences';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { treeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import {
  INITIAL_TREE,
  treeHasConditions,
  treeToSerializable,
} from '@/components/tree-dnd/tree-dnd-utils';

const PREVIEW_DEBOUNCE_MS = 500;

export function useCreateAudiencePage() {
  const tenantSlug = useParams().tenantSlug ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { LL, locale } = useI18nContext();
  const tree = useStore(treeDndStore, (state) => state.tree);
  const { mutateAsync: previewAudience } = usePreviewAudience();
  const { mutateAsync: createAudience } = useCreateAudience();

  const [name, setName] = useState<string>(LL.audiences.create.defaultName());
  const [nameError, setNameError] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [isSaveNameDialogOpen, setIsSaveNameDialogOpen] = useState(false);
  const [saveNameDialogName, setSaveNameDialogName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [targetUsersCount, setTargetUsersCount] = useState<
    number | undefined
  >();
  const [totalUserbaseCount, setTotalUserbaseCount] = useState(0);
  const [lastCalculatedTreeKey, setLastCalculatedTreeKey] = useState<
    string | null
  >(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewRequestIdRef = useRef(0);

  const treeKey = useMemo(
    () => JSON.stringify(treeToSerializable(tree)),
    [tree]
  );
  const hasFilter = treeHasConditions(tree);

  const targetCountStatus = useMemo((): AudienceTargetCountStatus => {
    if (!hasFilter) return 'empty';
    if (isPreviewLoading) return 'loading';
    if (lastCalculatedTreeKey === treeKey) return 'fresh';
    return 'stale';
  }, [hasFilter, isPreviewLoading, lastCalculatedTreeKey, treeKey]);

  const isCreateDisabled = !hasFilter || isCreating;

  const validateName = useCallback(
    (nameStr: string) => {
      if (!nameStr.trim()) {
        return LL.audiences.create.validation.nameRequired();
      }
      return '';
    },
    [LL]
  );

  const runPreview = useCallback(
    async (forTreeKey: string) => {
      const requestId = ++previewRequestIdRef.current;
      setIsPreviewLoading(true);

      try {
        const filters = mapTreeToFilterNodeDto(treeDndStore.getState().tree);
        const result = await previewAudience(filters);
        if (requestId !== previewRequestIdRef.current) return;

        setTargetUsersCount(result.targetedUserCount);
        setTotalUserbaseCount(result.notificationsEnabledUserCount);
        setLastCalculatedTreeKey(forTreeKey);
      } catch (error) {
        if (requestId !== previewRequestIdRef.current) return;

        setTargetUsersCount(undefined);
        setTotalUserbaseCount(0);
        setLastCalculatedTreeKey(null);

        toast.error(LL.general.error(), {
          description:
            error instanceof Error
              ? error.message
              : LL.audiences.create.toast.failedToCreateAudience(),
          duration: 3000,
        });
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setIsPreviewLoading(false);
        }
      }
    },
    [LL, previewAudience]
  );

  const handleRecalculateTargetCount = useCallback(() => {
    if (!hasFilter || isPreviewLoading) return;

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }

    void runPreview(treeKey);
  }, [hasFilter, isPreviewLoading, runPreview, treeKey]);

  useEffect(() => {
    if (!hasFilter) {
      previewRequestIdRef.current += 1;
      setTargetUsersCount(undefined);
      setTotalUserbaseCount(0);
      setLastCalculatedTreeKey(null);
      setIsPreviewLoading(false);
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      return;
    }

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    setIsPreviewLoading(true);
    previewTimeoutRef.current = setTimeout(() => {
      previewTimeoutRef.current = null;
      void runPreview(treeKey);
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
    };
  }, [hasFilter, runPreview, treeKey]);

  useEffect(() => {
    return () => {
      previewRequestIdRef.current += 1;
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      treeDndStore.getState().setTree(INITIAL_TREE);
    };
  }, []);

  const submitAudience = useCallback(
    async (audienceName: string) => {
      setIsCreating(true);
      try {
        const filters = mapTreeToFilterNodeDto(tree);
        const createdAudience = await createAudience({
          name: audienceName,
          type: 'filter',
          filters,
        });

        toast.success(LL.audiences.create.toast.audienceCreatedTitle(), {
          description: LL.audiences.create.toast.audienceCreatedDescription({
            audienceName,
          }),
          duration: 3000,
        });
        await queryClient.invalidateQueries({
          queryKey: ['audiences', tenantSlug],
        });
        navigate(`/${tenantSlug}/audiences/${createdAudience.id}`);
      } catch (error) {
        setIsCreating(false);
        toast.error(LL.general.error(), {
          description:
            error instanceof Error
              ? error.message
              : LL.audiences.create.toast.failedToCreateAudience(),
          duration: 3000,
        });
      }
    },
    [LL, createAudience, navigate, queryClient, tenantSlug, tree]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setNameEdited(true);
      setName(value);
      setNameError(validateName(value));
    },
    [validateName]
  );

  const handleCreate = useCallback(() => {
    if (!hasFilter || isCreating) return;

    if (nameEdited) {
      const error = validateName(name);
      setNameError(error);
      if (error) return;
      void submitAudience(name.trim());
      return;
    }

    setSaveNameDialogName(LL.audiences.create.defaultName());
    setIsSaveNameDialogOpen(true);
  }, [
    LL,
    hasFilter,
    isCreating,
    name,
    nameEdited,
    submitAudience,
    validateName,
  ]);

  const handleSaveNameConfirm = useCallback(
    (confirmedName: string) => {
      const trimmedName =
        confirmedName.trim() || LL.audiences.create.defaultName();
      const error = validateName(trimmedName);
      if (error) {
        setNameError(error);
        return;
      }

      handleNameChange(trimmedName);
      setIsSaveNameDialogOpen(false);
      void submitAudience(trimmedName);
    },
    [LL, handleNameChange, submitAudience, validateName]
  );

  const handleCancel = useCallback(() => {
    treeDndStore.getState().setTree(INITIAL_TREE);
    navigate(`/${tenantSlug}/audiences`);
  }, [navigate, tenantSlug]);

  return {
    tenantSlug,
    locale: locale as Locales,
    name,
    nameError,
    onNameChange: handleNameChange,
    targetCountStatus,
    targetUsersCount,
    totalUserbaseCount,
    onRecalculateTargetCount: handleRecalculateTargetCount,
    onCancel: handleCancel,
    onCreate: handleCreate,
    isCreating,
    isCreateDisabled,
    isSaveNameDialogOpen,
    onSaveNameDialogOpenChange: setIsSaveNameDialogOpen,
    saveNameDialogName,
    onSaveNameConfirm: handleSaveNameConfirm,
  };
}
